import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, Download, AlertCircle, CheckCircle2, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, Spinner, EmptyState } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtCurrency, fmtDate } from "@/lib/format";

export default function Deals() {
  const navigate = useNavigate();
  const { selected, isAll, currentClient } = useClient();
  const qc = useQueryClient();
  const single = !isAll && selected;

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deals", selected],
    queryFn: async () => (await api.get(`/clients/${selected}/deals`)).data,
    enabled: !!single,
  });

  const download = async () => {
    const res = await api.get(`/clients/${selected}/export`, { params: { dataset: "deals" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "deals.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (isAll) return <div><PageHeader title="Deals" /><EmptyState icon={AlertCircle} title="Select a client" subtitle="Choose a specific client to view its signed deals." /></div>;

  const totalValue = deals.reduce((s, d) => s + (d.value || 0), 0);
  const actualCount = deals.filter((d) => d.actual_deal_value).length;
  const influenced = deals.filter((d) => d.vsm_influenced).length;

  return (
    <div>
      <PageHeader title="Deals" subtitle={currentClient?.business_name}>
        <Button variant="outline" onClick={download} data-testid="export-deals-btn"><Download className="h-4 w-4 mr-1.5" /> Export</Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MiniStat label="Signed Deals" value={deals.length} />
        <MiniStat label="Total Value" value={fmtCurrency(totalValue)} />
        <MiniStat label="With Actual Value" value={`${actualCount} / ${deals.length}`} />
        <MiniStat label="VSM Influenced" value={influenced} accent />
      </div>

      {isLoading ? <Spinner /> : deals.length === 0 ? (
        <EmptyState icon={Briefcase} title="No deals yet" subtitle="Move a contact to the DEAL stage to record a signed deal." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Customer</TableHead><TableHead>Deal Date</TableHead><TableHead>Salesperson</TableHead>
                  <TableHead>Lead Source</TableHead><TableHead>VSM</TableHead><TableHead className="text-right">Value</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deals.map((d) => (
                  <TableRow key={d.id} data-testid={`deal-row-${d.id}`}>
                    <TableCell><button className="font-semibold text-slate-900 hover:text-blue-600" onClick={() => navigate(`/contacts/${d.id}`)}>{d.customer}</button></TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(d.deal_date)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{d.salesperson || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{d.original_lead_source || "—"}</TableCell>
                    <TableCell>{d.vsm_influenced ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline"><Sparkles className="h-3 w-3 mr-1" />Influenced</Badge> : <span className="text-slate-300 text-xs">—</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="font-bold tabular-nums text-slate-900">{fmtCurrency(d.value)}</div>
                      <div className={`text-[10px] font-bold uppercase ${d.value_type === "Actual" ? "text-emerald-600" : "text-amber-600"}`}>{d.value_type}</div>
                    </TableCell>
                    <TableCell><EditDealDialog clientId={selected} deal={d} onDone={() => qc.invalidateQueries({ queryKey: ["deals"] })} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-slate-100">
            {deals.map((d) => (
              <div key={d.id} data-testid={`deal-card-${d.id}`} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <button className="font-semibold text-slate-900 text-left hover:text-blue-600" onClick={() => navigate(`/contacts/${d.id}`)}>{d.customer}</button>
                  <div className="text-right shrink-0">
                    <div className="font-bold tabular-nums text-slate-900">{fmtCurrency(d.value)}</div>
                    <div className={`text-[10px] font-bold uppercase ${d.value_type === "Actual" ? "text-emerald-600" : "text-amber-600"}`}>{d.value_type}</div>
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-500">{fmtDate(d.deal_date)}{d.salesperson ? ` · ${d.salesperson}` : ""}{d.original_lead_source ? ` · ${d.original_lead_source}` : ""}</div>
                <div className="mt-2 flex items-center justify-between">
                  {d.vsm_influenced ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline"><Sparkles className="h-3 w-3 mr-1" />Influenced</Badge> : <span />}
                  <EditDealDialog clientId={selected} deal={d} onDone={() => qc.invalidateQueries({ queryKey: ["deals"] })} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (<div className={`rounded-xl border p-4 ${accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}>
    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
    <div className="text-xl font-heading font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
  </div>);
}

function EditDealDialog({ clientId, deal, onDone }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(deal.actual_deal_value || "");
  const [salesperson, setSalesperson] = useState(deal.salesperson || "");
  const mut = useMutation({
    mutationFn: async () => (await api.put(`/clients/${clientId}/deals/${deal.id}`, {
      actual_deal_value: value ? Number(value) : null, salesperson: salesperson || null,
    })).data,
    onSuccess: () => { toast.success("Deal updated"); setOpen(false); onDone(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`edit-deal-${deal.id}`}>Edit</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit Deal · {deal.customer}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Actual Deal Value</Label><Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Blank = use average job size" className="mt-1" data-testid="edit-deal-value" /></div>
          <div><Label>Salesperson</Label><Input value={salesperson} onChange={(e) => setSalesperson(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter><Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-deal-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
